import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import type { GridPaginationModel } from "@mui/x-data-grid-premium";
import { useAssessmentPlanFilterOptions } from "../../../hooks/useAssessmentPlanFilterOptions";
import { useAutoTaggingPlans } from "../../../hooks/useAutoTaggingPlans";
import {
  AUTO_TAGGING_PLANS_DEFAULT_PAGE_SIZE,
  normalizeAutoTaggingPlanNameQuery,
} from "../../../services/autoTaggingService";
import { AI_PLAN_ADD_ITEMS_PATH } from "../constants";
import TaggingPlanFilterBar, {
  EMPTY_TAGGING_PLAN_FILTERS,
  type TaggingPlanAppliedFilters,
} from "./TaggingPlanFilterBar";
import TaggingPlanTable from "./TaggingPlanTable";

export default function TaggingPlanTab() {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [appliedFilters, setAppliedFilters] = useState<TaggingPlanAppliedFilters>(
    EMPTY_TAGGING_PLAN_FILTERS,
  );
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: AUTO_TAGGING_PLANS_DEFAULT_PAGE_SIZE,
  });

  const apiConcept =
    appliedFilters.impactItemCodes.length === 1
      ? appliedFilters.impactItemCodes[0]
      : "";

  // Top-bar search → `keyword` (fuzzy); single Assessment name filter → `name` (exact).
  const apiKeyword = normalizeAutoTaggingPlanNameQuery(searchText);
  const apiName =
    appliedFilters.assessmentNames.length === 1
      ? normalizeAutoTaggingPlanNameQuery(appliedFilters.assessmentNames[0] ?? "")
      : "";

  const { planTypes, planTypesLoading, conceptOptions } =
    useAssessmentPlanFilterOptions("", {
      configurableOnly: true,
    });
  const {
    rows,
    total,
    loading: plansLoading,
    hasLoaded: plansHasLoaded,
    reload,
  } = useAutoTaggingPlans({
    concept: apiConcept,
    keyword: apiKeyword,
    name: apiName,
    planTypes,
    concepts: conceptOptions,
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
  });

  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 },
    );
  }, [appliedFilters, searchText]);

  const showInitialLoading =
    (!plansHasLoaded && plansLoading) || planTypesLoading;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        flex: 1,
        minHeight: 0,
        mx: "16px",
        mb: "16px",
      }}
    >
      <TaggingPlanFilterBar
        searchText={searchText}
        onSearchTextChange={setSearchText}
        appliedFilters={appliedFilters}
        onAppliedFiltersChange={setAppliedFilters}
      />
      {showInitialLoading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 320,
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            bgcolor: "#fff",
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TaggingPlanTable
          rows={rows}
          rowCount={total}
          loading={plansLoading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onAddNew={() => navigate(AI_PLAN_ADD_ITEMS_PATH)}
          onPlanDeleted={reload}
          onPlanUpdated={reload}
        />
      )}
    </Box>
  );
}
